import React, { KeyboardEvent, MouseEvent, useEffect, useRef, useState } from 'react'

import { IconCheck, IconClose, IconInfo, IconPencil } from '@avantstay/backoffice-vectors'

import { mainColors, semanticColors } from '../../styles'
import { Tooltip } from '../Tooltip'
import * as S from './EditableValue.styles'

interface EditableValueProps<ValueType> {
  value?: ValueType
  formatter?: (value: ValueType) => string
  editable?: boolean
  onChange?: (newValue: ValueType) => void
  previousValue?: ValueType
  color?: string
  className?: string
}

export function EditableValue<ValueType extends string | number>({
  className,
  color,
  editable = true,
  formatter,
  value,
  onChange,
  previousValue,
}: EditableValueProps<ValueType>) {
  const [isEditing, setIsEditing] = useState(false)
  const rootRef = useRef<HTMLDivElement | null>(null)

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!editable) {
      return
    }
    event.stopPropagation()
    setIsEditing(true)
  }

  useEffect(() => {
    if (isEditing && rootRef.current) {
      const range = document.createRange()
      const selection = window.getSelection()

      if (selection && value) {
        range.setStart(rootRef.current, 0)
        range.setEnd(rootRef.current, 1)
        selection.removeAllRanges()
        selection.addRange(range)
      }
      rootRef.current.focus()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, rootRef])

  const handleCancel = (event: MouseEvent<SVGSVGElement> | KeyboardEvent<HTMLDivElement>) => {
    event.stopPropagation()
    setIsEditing(false)
  }

  const handleApply = (event: MouseEvent<SVGSVGElement> | KeyboardEvent<HTMLDivElement>) => {
    event.stopPropagation()
    const newValue =
      typeof value === 'number'
        ? Number(rootRef.current?.innerText ?? value)
        : String(rootRef.current?.innerText ?? value)
    onChange?.(newValue as ValueType)
    setIsEditing(false)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      handleApply(event)
    }

    if (event.key === 'Escape') {
      handleCancel(event)
    }
  }

  const renderValue = () => {
    if (value === undefined || value === null || value === '') {
      return <span style={{ color: mainColors.neutralDark }}>no value</span>
    }
    return formatter ? formatter(value) : value
  }

  return (
    <S.Wrapper
      className={className}
      style={{ color: color || (previousValue ? semanticColors.warningDark : 'inherit') }}
    >
      <S.Root editable={editable} onClick={handleClick} isEditing={isEditing}>
        <S.EditableContent
          suppressContentEditableWarning={true}
          contentEditable={isEditing}
          ref={rootRef}
          onKeyDown={handleKeyDown}
        >
          {isEditing ? value : renderValue()}
        </S.EditableContent>
        {editable &&
          (isEditing ? (
            <>
              <IconCheck
                style={{ color: semanticColors.successDark }}
                width="20px"
                height="20px"
                onClick={handleApply}
              />
              <IconClose style={{ color: semanticColors.failureDark }} onClick={handleCancel} />
            </>
          ) : (
            <IconPencil className="pencil" />
          ))}
      </S.Root>

      {previousValue ? (
        <Tooltip
          content={`Previous value: ${formatter?.(previousValue) ?? previousValue}`}
          placement="top"
        >
          <IconInfo />
        </Tooltip>
      ) : null}
    </S.Wrapper>
  )
}
